import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Camera, UploadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { useSeo, breadcrumbs } from "@/lib/seo";
import { PublicShell, Crumbs } from "@/components/PublicShell";
import { ShareBar } from "@/components/ShareBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const LABEL = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function CastingPublic() {
  const { token } = useParams();
  const [call, setCall] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", country: "",
    height_cm: "", size: "", portfolio_url: "", instagram: "", availability: "", about: "" });
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get(`/casting/public/${token}`)
      .then(({ data }) => setCall(data))
      .catch((e) => setError(formatApiErrorDetail(e?.response?.data?.detail) || "That casting call is not open"));
  }, [token]);

  useSeo({
    title: call ? `${call.title} | Casting | Unity & Utility` : "Casting call | Unity & Utility",
    description: (call?.brief || "Open casting call at Unity & Utility.").slice(0, 180),
    path: `/casting/${token}`,
    jsonLd: breadcrumbs([["Home", "/"], ["Fashion trends", "/fashion-trends"], [call?.title || "Casting", null]]),
  });

  const addPhoto = async (file) => {
    if (!file) return;
    if (photos.length >= 3) { toast.error("Up to three photos"); return; }
    const body = new FormData();
    body.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post(`/casting/public/${token}/photo`, body,
        { headers: { "Content-Type": "multipart/form-data" } });
      setPhotos((p) => [...p, { id: data.id, name: file.name }]);
      toast.success("Photo attached");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/casting/public/${token}/apply`, {
        ...form,
        height_cm: form.height_cm ? Number(form.height_cm) : null,
        photo_ids: photos.map((p) => p.id),
      });
      setDone(data);
      toast.success("Application sent");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const field = (key, label, props = {}) => (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input data-testid={`casting-${key}`} value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })} {...props} />
    </div>
  );

  return (
    <PublicShell>
      <div className="mx-auto max-w-3xl px-5 py-10 sm:px-8 sm:py-14" data-testid="casting-public-page">
        <Crumbs items={[["Home", "/"], ["Fashion trends", "/fashion-trends"], [call?.title || "Casting", null]]} />
        {error && <p data-testid="casting-error" className="mt-6 text-sm text-slate-500">{error}</p>}
        {call && (
          <>
            <p className="overline text-[#B3241B]">{call.role_label} · {LABEL(call.segment)}</p>
            <h1 data-testid="casting-title" className="font-display mt-2 flex items-start gap-3 text-3xl font-bold leading-[1.1] tracking-tight text-[#0F172A] sm:text-4xl">
              <Camera size={34} className="mt-1 shrink-0 text-[#002FA7]" weight="duotone" />
              {call.title}
            </h1>
            <p className="mt-3 text-[11px] uppercase tracking-wide text-slate-500">
              {[call.city, call.country, call.pay_note,
                call.closing_date ? `applications close ${call.closing_date}` : null]
                .filter(Boolean).join(" · ")}
            </p>
            {call.brief && <p data-testid="casting-brief" className="mt-4 whitespace-pre-line text-base leading-relaxed text-slate-700">{call.brief}</p>}

            <div className="mt-6">
              <ShareBar url={`${BACKEND}/api/share/casting/${token}`} text={`${call.role_label} casting — ${call.title}`}
                testid="casting-share" instagram compact />
            </div>

            {done ? (
              <p data-testid="casting-applied" className="mt-8 border-l-2 border-[#0F7A3D] bg-[#EAF7EF] px-4 py-3 text-sm text-[#0B5C2E]">
                Thank you {done.name} — {done.message}
              </p>
            ) : (
              <div className="mt-8 border border-border bg-white p-5" data-testid="casting-form">
                <p className="text-sm font-semibold text-[#0F172A]">Apply for this</p>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {field("name", "Your name")}
                  {field("email", "Email", { type: "email" })}
                  {field("phone", "Phone")}
                  {field("city", "City")}
                  {field("country", "Country")}
                  {field("height_cm", "Height (cm)", { type: "number" })}
                  {field("size", "Size you wear")}
                  {field("instagram", "Instagram handle")}
                  {field("portfolio_url", "Portfolio link")}
                  {field("availability", "Dates you are free")}
                  <div className="sm:col-span-2">
                    <Label className="text-xs">Anything else we should know</Label>
                    <Textarea data-testid="casting-about" rows={3} value={form.about}
                      onChange={(e) => setForm({ ...form, about: e.target.value })} />
                  </div>
                </div>

                <div className="mt-4">
                  <Label className="text-xs">Photos (up to 3)</Label>
                  <input ref={fileRef} data-testid="casting-photo-input" type="file" accept="image/*"
                    className="hidden" onChange={(e) => addPhoto(e.target.files?.[0])} />
                  <button type="button" data-testid="casting-photo-add" disabled={busy || photos.length >= 3}
                    onClick={() => fileRef.current?.click()}
                    className="tap-safe mt-1 inline-flex items-center gap-1.5 border border-border px-3 py-2 text-xs font-semibold text-slate-600 hover:border-[#002FA7] hover:text-[#002FA7] disabled:opacity-60">
                    <UploadSimple size={14} /> {busy ? "Uploading…" : "Add a photo"}
                  </button>
                  {photos.length > 0 && (
                    <ul data-testid="casting-photo-list" className="mt-2 space-y-1 text-xs text-slate-600">
                      {photos.map((p) => <li key={p.id}>· {p.name}</li>)}
                    </ul>
                  )}
                  <p className="mt-2 text-[11px] text-slate-500">
                    Your details and photos go only to the casting team — nothing is shown publicly.
                  </p>
                </div>

                <Button className="mt-4 w-full sm:w-auto" data-testid="casting-submit" onClick={submit}
                  disabled={busy || form.name.trim().length < 2 || !form.email.includes("@")}>
                  {busy ? "Sending…" : "Send my application"}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </PublicShell>
  );
}
